﻿import { useFlightWorker } from './use-flight-worker';
import { useAppSelector } from './store-hooks';
import { TimeSettings, UnitSettings } from '../../state/settings';
import { shallowEqual } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { MessageType } from '../../services/actor-messages';
import { envConfig } from '../../configs/env';

export default function useAirportSummaryUrl(airportId: string|null) {
    const worker = useFlightWorker();
    const [url, setUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isDark = useAppSelector(state => state.settings.isDark);

    const timeSettings: TimeSettings = useAppSelector(
        (state) => ({
            timeZone: state.settings.timeDisplayZone,
            timeFormat: state.settings.timeDisplayFormat,
            dateFormat: state.settings.dateDisplayFormat,
            dateDelimiter: state.settings.dateDelimiter,
            showDaysWithTime: state.settings.showDayWithTimes
        }),
        shallowEqual
    );

    const unitSettings: UnitSettings = useAppSelector(
        (state) => ({
            fobVolume: state.settings.flightFobVolumeDisplayUnit,
            fobWeight: state.settings.flightFobWeightDisplayUnit,
            latLngFormat: state.settings.latLongDisplayFormat,
            distanceUnit: state.settings.distanceUnit
        }),
        shallowEqual
    );

    const buildTimeSettings = useCallback(() => {
        return (
            `` +
            `&timeZone=${timeSettings.timeZone}` +
            `&timeFormat=${timeSettings.timeFormat}` +
            `&dateFormat=${timeSettings.dateFormat}` +
            `&dateDelimiter=${timeSettings.dateDelimiter}` +
            `&showDaysWithTime=${timeSettings.showDaysWithTime}`
        );
    }, [timeSettings]);

    const buildUnitSettings = useCallback(() => {
        return (
            `` +
            `&fobVolume=${unitSettings.fobVolume}` +
            `&fobWeight=${unitSettings.fobWeight}` +
            `&latLngFormat=${unitSettings.latLngFormat}` +
            `&distanceUnit=${unitSettings.distanceUnit}`
        );
    }, [unitSettings]);

    const buildUrl = useCallback(async () => {
        if(!airportId){
            setError('Invalid airport ID. Please check the ID and try again');
            setUrl('');
            return;
        }
        const airport = await worker.actor.sendAsync({ type: MessageType.findAirport, data: airportId });
        const id = airport?.icao ?? airport?.faa ?? airport?.iata;
        if (!id) {
            setError('We cannot find an airport with the provided ID. Please check the ID and try again.');
            setUrl('');
            return;
        }
        const url =
            `${envConfig.service}/airport-summary/?airportId=${id}&isDark=${isDark}&showThemeToggle=false` +
            buildTimeSettings() +
            buildUnitSettings();
        setError(null);
        setUrl(url);
    }, [airportId,isDark, worker, buildTimeSettings, buildUnitSettings]);

    useEffect(() => {
        if(airportId === null){
            return 
        }
        let cancelled = false;
        setLoading(true);
        buildUrl().finally(() => {
            if (!cancelled) {
                setLoading(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [buildUrl]);

    return { url, error, loading };
}